import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import * as UITooltip from '@/components/ui/tooltip'
import {
  Phone,
  ArrowUpRight,
  Zap,
  Activity,
  BarChart3,
  Clock,
  Info,
} from 'lucide-react'
import {
  formatNumber,
  formatCallDuration,
  formatDateRangeForDisplay,
  safeNumber,
} from '@/utils/reporting-formatters'
import { SingleChart } from './ChartComponents'
import { MetricCard } from '@/components/ui/metric-card'
import { buildChartData } from '@/utils/build-chart-data'
import type {
  CallReportResponse,
  DirectionCallMetrics,
  ActivityReportResponse,
  ActionMetricsResponse,
} from '@/lib/api/reporting.client'
import { Heading, Text } from '@/components/ui/typography'
import { useState } from 'react'

export type ReportDirection = 'total' | 'inbound' | 'outbound'

const DIRECTION_OPTIONS: { value: ReportDirection; label: string }[] = [
  { value: 'total', label: 'Total' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
]

const DIRECTION_CALLS_LABEL: Record<ReportDirection, string> = {
  total: 'Total Calls',
  inbound: 'Inbound Calls',
  outbound: 'Outbound Calls',
}

const DIRECTION_SCOPE_TEXT: Record<ReportDirection, string> = {
  total: 'all AI-handled calls',
  inbound: 'inbound calls answered by AI',
  outbound: 'outbound calls placed by AI',
}

interface ChartSeriesConfig {
  dataKey1: string
  color1: string
  name1: string
  dataKey2?: string
  color2?: string
  name2?: string
}

const CHART_SERIES: Record<ReportDirection, ChartSeriesConfig> = {
  total: {
    dataKey1: 'callsHandledByAI',
    color1: '#3750B6',
    name1: 'Inbound calls',
    dataKey2: 'callsOutbound',
    color2: '#F59E0B',
    name2: 'Outbound calls',
  },
  inbound: {
    dataKey1: 'callsHandledByAI',
    color1: '#3750B6',
    name1: 'Inbound calls',
  },
  outbound: {
    dataKey1: 'callsOutbound',
    color1: '#F59E0B',
    name1: 'Outbound calls',
  },
}

function selectMetrics(
  callReport: CallReportResponse,
  direction: ReportDirection,
): DirectionCallMetrics {
  if (direction === 'total') return callReport.total
  if (direction === 'outbound') return callReport.outbound
  return callReport
}

interface CallsTabProps {
  callReport: CallReportResponse | null
  activityReport: ActivityReportResponse | null
  actionMetrics: ActionMetricsResponse | null
  dateRange: string
  appliedCustomDates: { start: string; end: string } | null
  chartType: 'line' | 'bar'
  setChartType: (type: 'line' | 'bar') => void
  isLoading: boolean
}

export function CallsTab({
  callReport,
  activityReport,
  actionMetrics,
  dateRange,
  appliedCustomDates,
  chartType,
  setChartType,
  isLoading,
}: CallsTabProps) {
  const [direction, setDirection] = useState<ReportDirection>('total')
  const dateRangeText = formatDateRangeForDisplay(dateRange, appliedCustomDates)
  const chartData = buildChartData(activityReport, dateRange)
  const metrics = callReport ? selectMetrics(callReport, direction) : null
  const hasCallData =
    metrics != null &&
    (safeNumber(metrics.handledByAI) > 0 ||
      safeNumber(metrics.resolvedCalls) > 0)
  const scopeText = DIRECTION_SCOPE_TEXT[direction]

  const chartSeries = CHART_SERIES[direction]

  const dataAvailabilityNote = metrics?.earliestDataDate
    ? ` Data available as of ${metrics.earliestDataDate}.`
    : ''

  // Calls without AI-led time (e.g. from before the timestamps existed) are
  // "not measured", not zero: show a dash and say how many calls the value covers.
  const totalCalls = safeNumber(metrics?.handledByAI)
  const callsWithAiTalkTime =
    metrics?.callsWithAiTalkTime ??
    (safeNumber(metrics?.totalAiTalkTime) > 0 ? totalCalls : 0)
  const hasAiTalkTime = callsWithAiTalkTime > 0
  const aiTalkTimeCoverage = !hasAiTalkTime
    ? totalCalls > 0
      ? 'No AI talk time data yet'
      : null
    : callsWithAiTalkTime < totalCalls
      ? `Based on ${formatNumber(callsWithAiTalkTime)} of ${formatNumber(totalCalls)} calls`
      : null
  const aiTalkTimeCoverageNote = aiTalkTimeCoverage
    ? ` ${formatNumber(totalCalls - callsWithAiTalkTime)} of ${formatNumber(totalCalls)} calls have no AI talk time data, so they are not included.`
    : ''
  const noAiTalkTimeText = `No calls in ${dateRangeText} have AI talk time data.`

  // The API applies the cutover to callsTransferred. The action-metrics
  // fallback (older API) does not, so it gets no "Data available as of" note.
  const callsTransferred =
    callReport?.callsTransferred ?? actionMetrics?.byType.PHONE_CALL ?? 0
  const callsTransferredNote =
    callReport?.callsTransferred !== undefined ? dataAvailabilityNote : ''

  const cutoverDate = callReport?.earliestDataDate
    ? new Date(`${callReport.earliestDataDate}T00:00:00Z`).toLocaleDateString(
        'en-US',
        { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' },
      )
    : null

  return (
    <div className="space-y-6 mt-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Text variant="muted">
            How each metric breaks down in {dateRangeText}
          </Text>
          {cutoverDate && (
            <UITooltip.TooltipProvider>
              <UITooltip.Tooltip>
                <UITooltip.TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:border-border dark:bg-muted dark:text-muted-foreground"
                  >
                    <Info className="h-3 w-3" />
                    Data from {cutoverDate}
                  </button>
                </UITooltip.TooltipTrigger>
                <UITooltip.TooltipContent className="max-w-xs">
                  <p>
                    Call summaries now include both inbound and outbound calls
                    starting {cutoverDate}. Need historical data? Contact
                    help@hiresuper.com.
                  </p>
                </UITooltip.TooltipContent>
              </UITooltip.Tooltip>
            </UITooltip.TooltipProvider>
          )}
        </div>
        {!isLoading && callReport && (
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-muted p-1 rounded-lg w-fit">
            {DIRECTION_OPTIONS.map(option => (
              <Button
                key={option.value}
                variant={direction === option.value ? 'default' : 'ghost'}
                size="sm"
                className="h-8 px-3"
                onClick={() => setDirection(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {isLoading ? (
          [1, 2, 3, 4].map(i => (
            <Card key={i} className="border border-gray-200">
              <CardContent className="p-3 relative">
                <Skeleton className="absolute -top-2 -left-2 h-8 w-8 rounded-lg" />
                <Skeleton className="h-4 w-24 mx-auto mb-3 mt-2" />
                <Skeleton className="h-10 w-16 mx-auto mb-3" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))
        ) : callReport && metrics ? (
          <>
            <MetricCard
              icon={Zap}
              title="AI Talk Time"
              metric={
                hasAiTalkTime
                  ? formatCallDuration(metrics.totalAiTalkTime ?? 0)
                  : '—'
              }
              description={aiTalkTimeCoverage ?? 'Time the AI led the call'}
              info={`AI Talk Time is the total time the AI led the call across ${scopeText}. ${hasAiTalkTime ? `Total AI talk time was ${formatCallDuration(metrics.totalAiTalkTime ?? 0)} in ${dateRangeText}.` : noAiTalkTimeText}${aiTalkTimeCoverageNote}${dataAvailabilityNote}`}
            />
            <MetricCard
              icon={Phone}
              title={DIRECTION_CALLS_LABEL[direction]}
              metric={formatNumber(metrics.handledByAI)}
              description="Total calls answered by AI"
              info={`${DIRECTION_CALLS_LABEL[direction]} measures the total number of ${scopeText}. ${formatNumber(metrics.handledByAI)} calls in ${dateRangeText}.${dataAvailabilityNote}`}
            />
            <MetricCard
              icon={ArrowUpRight}
              title="Calls Transferred"
              metric={formatNumber(callsTransferred)}
              description="Escalated to live agents"
              info={`Calls transferred measures the number of calls where AI transferred to a live agent — not broken down by direction. ${formatNumber(callsTransferred)} calls were transferred in ${dateRangeText}.${callsTransferredNote}`}
            />
            <MetricCard
              icon={Clock}
              title="Avg AI Talk Time"
              metric={
                hasAiTalkTime
                  ? formatCallDuration(metrics.averageAiTalkTime ?? 0)
                  : '—'
              }
              description={
                aiTalkTimeCoverage ?? 'Typical time the AI led a call'
              }
              info={`Average AI talk time is the typical time the AI led a call, among ${scopeText} that have AI talk time data. ${hasAiTalkTime ? `The average was ${formatCallDuration(metrics.averageAiTalkTime ?? 0)} in ${dateRangeText}.` : noAiTalkTimeText}${aiTalkTimeCoverageNote}${dataAvailabilityNote}`}
            />
          </>
        ) : (
          <Card className="col-span-full text-center border border-gray-200 rounded-lg p-4">
            <div className="py-12">
              <Text variant="muted">
                No call data available for the selected date range.
              </Text>
            </div>
          </Card>
        )}
      </div>

      {/* Activity Chart */}
      <div className="space-y-6">
        {/* Chart Type Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <Heading variant="section-title" as="h4">
              Daily calls activity
            </Heading>
            <Text variant="muted" className="mt-1">
              Calls handled over time
            </Text>
          </div>
          {!isLoading && (
            <UITooltip.TooltipProvider>
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-muted p-1 rounded-lg w-fit">
                <UITooltip.Tooltip>
                  <UITooltip.TooltipTrigger asChild>
                    <Button
                      variant={chartType === 'line' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setChartType('line')}
                    >
                      <Activity className="h-4 w-4" />
                    </Button>
                  </UITooltip.TooltipTrigger>
                  <UITooltip.TooltipContent>
                    <p>Line Chart - Shows trends over time</p>
                  </UITooltip.TooltipContent>
                </UITooltip.Tooltip>
                <UITooltip.Tooltip>
                  <UITooltip.TooltipTrigger asChild>
                    <Button
                      variant={chartType === 'bar' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => setChartType('bar')}
                    >
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                  </UITooltip.TooltipTrigger>
                  <UITooltip.TooltipContent>
                    <p>Bar Chart - Compares values across periods</p>
                  </UITooltip.TooltipContent>
                </UITooltip.Tooltip>
              </div>
            </UITooltip.TooltipProvider>
          )}
        </div>

        <SingleChart
          title=""
          description=""
          {...chartSeries}
          icon={Phone}
          hasData={hasCallData ?? false}
          chartType={chartType}
          chartData={chartData}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
